import { Helmet } from 'react-helmet-async';
import { Mail } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const DATA_DELETION_EMAIL = 'support@schoolola.com.au';

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 className="font-heading text-2xl md:text-3xl font-medium text-foreground mb-4">
    {children}
  </h2>
);

const BulletList = ({ items }: { items: React.ReactNode[] }) => (
  <ul className="space-y-3">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3 text-foreground/80 leading-relaxed">
        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" aria-hidden />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const DataDeletion = () => (
  <>
    <Helmet>
      <title>Request data deletion | Schoolola Tutoring</title>
      <meta
        name="description"
        content="Request deletion of data held by Schoolola, including Project SID games portal data. Learn what we hold and how to ask for deletion."
      />
    </Helmet>

    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-4 py-12 md:py-20 max-w-3xl">
        <div className="space-y-4 mb-10">
          <h1 className="font-heading text-4xl md:text-5xl font-medium text-foreground">
            Request data deletion
          </h1>
          <p className="text-lg text-foreground/80 leading-relaxed">
            This page explains how to request deletion of data held by Schoolola,
            including data from the Project SID games portal.
          </p>
        </div>

        <section className="mb-12">
          <SectionHeading>What we hold</SectionHeading>
          <BulletList
            items={[
              <>
                <strong className="text-foreground">Staff accounts:</strong> an email address and
                a password for centre managers and administrators.
              </>,
              <>
                <strong className="text-foreground">Game data:</strong> for each round a child plays,
                the animal name and emoji they chose, the team they were in, the words they found, and
                the points scored. This is what produces the report a tutor reviews after the lesson.
              </>,
              <>
                <strong className="text-foreground">Children do not have accounts.</strong> No name,
                date of birth, contact details or any other personal information is collected from a child.
              </>,
            ]}
          />
        </section>

        <section className="mb-12">
          <SectionHeading>How to request deletion</SectionHeading>
          <p className="text-foreground/80 leading-relaxed mb-6">
            To request deletion of any data we hold, email{' '}
            <a
              href={`mailto:${DATA_DELETION_EMAIL}?subject=Data deletion request`}
              className="inline-flex items-center gap-1 text-primary hover:underline underline-offset-2 font-medium"
            >
              <Mail className="h-4 w-4" />
              {DATA_DELETION_EMAIL}
            </a>{' '}
            with the subject &quot;Data deletion request&quot; and tell us which centre and which data
            you are asking about.
          </p>
          <p className="text-foreground/80 leading-relaxed">
            We will confirm receipt within two business days and complete the deletion within 30 days.
          </p>
        </section>

        <section>
          <SectionHeading>What happens when data is deleted</SectionHeading>
          <p className="text-foreground/80 leading-relaxed mb-6">
            Deleting a round removes the words found and the scores for every child in it, so the report
            for that lesson is permanently lost. We will confirm this with you before proceeding.
          </p>
          <p className="text-foreground/80 leading-relaxed">
            Staff accounts are deleted along with their access to every centre.
          </p>
        </section>
      </main>

      <Footer />
    </div>
  </>
);

export default DataDeletion;
